import { NgClass, NgTemplateOutlet } from '@angular/common';
import { AfterViewInit, Component, DestroyRef, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, Renderer2, SimpleChanges, booleanAttribute, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatTabsModule } from '@angular/material/tabs';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { environment } from '../../../../../environments/environment';
import { APPOINTMENT_CONSTANT } from '../../../../constants/appointment-constants';
import { TRANSLATION_TEMPLATES } from '../../../../constants/translation-templates-constants';
import { MAX_SLOTS_VISIBLE_LENGTH, WIDGET_CONSTANTS } from '../../../../constants/widget-constants';
import { AppointmentsSlotsDao } from '../../../../db-models/free-appointment-dao';
import { WidgetAppointmentGroupModel } from '../../../../db-models/widget-appointment-group.dao';
import { CustomBookingMessageTemplate } from '../../../../db-models/widget-conf-dao';
import { CartItem } from '../../../../models/cart.model';
import { Partner } from '../../../../models/global';
import { AppointmentState, ConferenceState } from '../../../../models/state.model';
import { WidgetColorConf } from '../../../../models/widget-color.model';
import { AppointmentService } from '../../../../services/appointment.service';
import { CustomEventService } from '../../../../services/custom-event.service';
import { DateUtilsService } from '../../../../services/date-utils.service';
import { HelperService } from '../../../../services/helper.service';
import { LoggerService } from '../../../../services/logger.service';
import { WidgetUtilService } from '../../../../services/widget-util.service';
import { AlertComponent } from '../../../../shared/components/alert/alert.component';
import { DateUtcPipe } from '../../../../shared/pipes/date_utc.pipe';
import { TranslationPipe } from '../../../../shared/pipes/translation.pipe';
import { TrustHtmlPipe } from '../../../../shared/pipes/trust-html.pipe';
import { CwAttributeTooptipComponent } from '../../../common/theme/cw-attribute-tooptip/cw-attribute-tooptip.component';
import { CwCheckboxRadioButtonComponent } from '../../../common/theme/cw-checkbox-radio-button/cw-checkbox-radio-button.component';
import { getDomRoot } from '../../../../services/dom-root.util';

const components = [AlertComponent, CwCheckboxRadioButtonComponent, CwAttributeTooptipComponent];
const modules = [MatTabsModule, FontAwesomeModule, TranslateModule, FormsModule];
const pipes = [DateUtcPipe, TranslationPipe, TrustHtmlPipe];

@Component({
  selector: 'app-appointment-card',
  templateUrl: './appointment-card.component.html',
  styleUrls: ['./appointment-card.component.scss'],
  imports: [NgTemplateOutlet, NgClass, ...components, ...modules, ...pipes]
})
export class AppointmentCardComponent implements OnInit, OnChanges, AfterViewInit {

  @Input() freeAppointments: {
    dayPeriod: string,
    appointments: AppointmentsSlotsDao[],
    showDayPeriodLabel: boolean,
    widgetGroup: WidgetAppointmentGroupModel,
    loadMore?: boolean
    selectedSlots?: number
  }[];
  @Input() appointmentState: AppointmentState;
  @Input() noAppointments: boolean;
  @Input() cart: CartItem[];
  @Input() partner: Partner;
  @Input() lang: string;
  @Input() widgetColorConf?: WidgetColorConf;
  @Input() showEgalWer: boolean;
  @Input() freeAppointmentsCount = 0;
  @Input() widgetTemplates: CustomBookingMessageTemplate[] = [];
  @Input() conferenceState: ConferenceState;
  @Input({
    transform: booleanAttribute
  }) showAppointmentSlots = false;

  @Output() updateCartEvent = new EventEmitter<{ event: any; appointment: AppointmentsSlotsDao; }>();
  @Output() gotoNextPageEvent = new EventEmitter<void>();

  private dateUtilsService = inject(DateUtilsService);

  readonly maxSlotsVisibleLength = MAX_SLOTS_VISIBLE_LENGTH;
  readonly appointmentConstant = APPOINTMENT_CONSTANT;
  readonly deployUrl = environment.deployUrl;
  readonly templateContent = TRANSLATION_TEMPLATES;

  get timeFormat(): string {
    return this.dateUtilsService.userTimeFormat.moment;
  }

  loadMoreSlots = false;
  widgetMorningLabelTemplate: CustomBookingMessageTemplate;
  widgetForenoonLabelTemplate: CustomBookingMessageTemplate;
  widgetNoonLabelTemplate: CustomBookingMessageTemplate;
  widgetAfternoonLabelTemplate: CustomBookingMessageTemplate;
  widgetEveningLabelTemplate: CustomBookingMessageTemplate;
  widgetNightLabelTemplate: CustomBookingMessageTemplate;
  noFreeAppointments: CustomBookingMessageTemplate;
  selectedSlotBackgroundColor = '#ebf1fb';
  selectedSlotBorderColor = '1px solid #3A4A66';
  cartSupported: boolean;
  hide_widget_cart_functionality_message: number;
  hasGroups = false;
  availableSlotsCount: number;
  protected isTroubleshootEnabled = false;

  private appointmentService = inject(AppointmentService);
  private widgetUtilService = inject(WidgetUtilService);
  private helperService = inject(HelperService);
  private translate = inject(TranslateService);
  private _renderer = inject(Renderer2);
  private elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private destroyRef = inject(DestroyRef);
  private customEventService = inject(CustomEventService);

  ngOnInit(): void {
    this.translate.onLangChange.subscribe(lang => this.lang = lang.lang);
    this.cartSupported = this.widgetUtilService.getWidgetConf().cartSupported;
    this.hide_widget_cart_functionality_message = this.widgetUtilService.getWidgetConf().hide_widget_cart_functionality_message;
    this.availableSlotsCount = this.freeAppointments[0]?.appointments?.length >= this.maxSlotsVisibleLength ? 10 : this.maxSlotsVisibleLength;

    this.freeAppointments.forEach(freeAppointmentGroup => {
      this.updateGroups(freeAppointmentGroup);
    });

    this.customEventService.toggleTroubleshootEvent
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(state => this.isTroubleshootEnabled = state);

    this.appointmentService.resetSlotSettingsEvent
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.freeAppointments.forEach(freeAppointmentGroup => {
          this.updateGroups(freeAppointmentGroup);
        });
      });
  }

  updateGroups(freeAppointmentGroup: {
    dayPeriod: string,
    appointments: AppointmentsSlotsDao[],
    showDayPeriodLabel: boolean,
    widgetGroup: WidgetAppointmentGroupModel,
    loadMore?: boolean
    selectedSlots?: number
  }): void {
    if (freeAppointmentGroup.widgetGroup?.only_show_one_element === 1) {
      freeAppointmentGroup.appointments = (freeAppointmentGroup.widgetGroup?.direction === 'desc')
        ? freeAppointmentGroup.appointments.slice(-1)
        : freeAppointmentGroup.appointments.slice(0, 1);
    } else {
      if (freeAppointmentGroup.widgetGroup?.direction === 'desc') {
        freeAppointmentGroup.appointments = freeAppointmentGroup.appointments.reverse();
      }
    }

    freeAppointmentGroup.loadMore = false;
    freeAppointmentGroup.selectedSlots = 0;
    freeAppointmentGroup.widgetGroup && (this.hasGroups = true);
  }

  ngAfterViewInit(): void {
    const tabInkBar = getDomRoot(this.elementRef.nativeElement).querySelector('mat-ink-bar');
    if (tabInkBar) {
      this._renderer.addClass(tabInkBar, 'csw-slot-selected-tab');
      this.updateTabsCounter();
    }
  }

  updateCart(event: any, appointment: AppointmentsSlotsDao): void {
    if (this.isTroubleshootEnabled) {
      appointment.isExpanded = !appointment.isExpanded;
    } else {
      !this.cartSupported && (this.cart = []);
      this.updateCartEvent.emit({ event, appointment });
    }
  }

  updateCartManually(id: string): void {
    const cartElement = getDomRoot(this.elementRef.nativeElement).getElementById(id);
    if (!cartElement) {
      LoggerService.warn('[appointment-card] cart element not found', id);
      return;
    }
    cartElement.click();
  }

  isTerminInCart(cartItemId: string): boolean {
    if (this.cart != null) {
      for (let i = this.cart.length - 1; i >= 0; i--) {
        if (this.cart[i].cartItemId === cartItemId) {
          return true;
        }
      }
    }
    return false;
  }

  terminConflict(appointment: AppointmentsSlotsDao, cartItemId: string): boolean {
    for (const cartItem of this.cart) {
      // Skip checking against itself
      if (cartItem.appointmentDaoObject.appointmentOrderIndex === appointment.appointmentOrderIndex) {
        continue;
      }

      const hasTimeOverlap = (
        (appointment.start >= cartItem.startTime && appointment.start < cartItem.endTime) ||
        (appointment.start < cartItem.startTime && appointment.end > cartItem.startTime)
      );

      if (hasTimeOverlap) {
        // Regular booking: time overlap alone is a conflict
        if (!this.appointmentState?.isCombiServiceSelected) {
          return true;
        }

        // Combi-service: time overlap + shared worker is a conflict
        const appointmentWorkerIds = Object.values(appointment.combiServiceWorkersMapping || {}).map(Number);
        const cartItemWorkerIds = Object.values(cartItem.combiServiceWorkersMapping || {}).map(Number);

        if (appointmentWorkerIds.some(id => cartItemWorkerIds.includes(id))) {
          return true;
        }
      }
    }

    return false;
  }

  createCartItemId(appointment: AppointmentsSlotsDao): string {
    return this.appointmentService.createCartItemId(appointment, this.appointmentState?.date);
  }

  updateTabsCounter(): void {
    this.freeAppointments.forEach(freeAppointmentGroup => {
      const selectedAppointments = freeAppointmentGroup.appointments.filter(appointment => {
        return this.isTerminInCart(this.createCartItemId(appointment));
      });
      freeAppointmentGroup.selectedSlots = (selectedAppointments?.length ? selectedAppointments.length : 0);
    });
  }

  loadMore(loadMoreIndex: number): void {
    this.freeAppointments.forEach((freeAppointmentGroup, index) => {
      if (loadMoreIndex === index) {
        freeAppointmentGroup.loadMore = true;
        return;
      }
    });
  }

  gotoNextPage(): void {
    this.gotoNextPageEvent.emit();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.widgetTemplates?.currentValue) {
      this.widgetTemplates?.length && this.setupTemplates();
    }

    if (changes?.cart?.currentValue) {
      this.updateTabsCounter();
    }
  }

  setupTemplates(): void {
    this.widgetMorningLabelTemplate = this.helperService.findTemplate(
      this.widgetTemplates, WIDGET_CONSTANTS.WIDGET_LABELS.WIDGET_MORNING_LABEL
    );

    this.widgetForenoonLabelTemplate = this.helperService.findTemplate(
      this.widgetTemplates, WIDGET_CONSTANTS.WIDGET_LABELS.WIDGET_FORENOON_LABEL
    );

    this.widgetNoonLabelTemplate = this.helperService.findTemplate(
      this.widgetTemplates, WIDGET_CONSTANTS.WIDGET_LABELS.WIDGET_NOON_LABEL
    );

    this.widgetAfternoonLabelTemplate = this.helperService.findTemplate(
      this.widgetTemplates, WIDGET_CONSTANTS.WIDGET_LABELS.WIDGET_AFTERNOON_LABEL
    );

    this.widgetEveningLabelTemplate = this.helperService.findTemplate(
      this.widgetTemplates, WIDGET_CONSTANTS.WIDGET_LABELS.WIDGET_EVENING_LABEL
    );

    this.widgetNightLabelTemplate = this.helperService.findTemplate(
      this.widgetTemplates, WIDGET_CONSTANTS.WIDGET_LABELS.WIDGET_NIGHT_LABEL
    );

    this.noFreeAppointments = this.helperService.findTemplate(
      this.widgetTemplates, WIDGET_CONSTANTS.WIDGET_LABELS.WIDGET_NO_POSSIBLE_APPOINTMENTS_HINT
    );
  }
}
